"use client"

import { useState, useEffect, useRef } from "react"
import dynamic from "next/dynamic"

const Confetti = dynamic(() => import("react-confetti"), { ssr: false })

interface ConfettiBurstProps {
    /** When true, triggers the confetti burst. Fires on each false→true transition. */
    trigger?: boolean
    /** Callback when confetti animation completes */
    onComplete?: () => void
    /** X coordinate of confetti source (center of burst) */
    sourceX?: number
    /** Y coordinate of confetti source (launch point - confetti shoots UP from here) */
    sourceY?: number
    /** Width of the spawn area */
    sourceWidth?: number
    /** Z-index for the confetti canvas */
    zIndex?: number
}

/**
 * ConfettiBurst - Standalone confetti component for landing page celebrations
 *
 * Creates a firework-like effect: confetti bursts UPWARD from the source point,
 * rises into the air, then falls naturally with gravity.
 * Fires on every false→true trigger transition.
 */
export function ConfettiBurst({
    trigger = false,
    onComplete,
    sourceX,
    sourceY,
    sourceWidth = 280,
    zIndex = 50
}: ConfettiBurstProps) {
    const [showConfetti, setShowConfetti] = useState(false)
    const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })
    const prevTriggerRef = useRef(false)

    // Track window size for canvas dimensions
    useEffect(() => {
        const updateSize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight })
        }
        updateSize()
        window.addEventListener("resize", updateSize)
        return () => window.removeEventListener("resize", updateSize)
    }, [])

    // Handle trigger - fires on every false→true transition (rising edge)
    useEffect(() => {
        const wasTriggered = !prevTriggerRef.current && trigger
        prevTriggerRef.current = trigger

        if (wasTriggered && windowSize.width > 0) {
            setShowConfetti(true)

            // Auto cleanup after animation
            const timeout = setTimeout(() => {
                setShowConfetti(false)
                onComplete?.()
            }, 2800)

            return () => clearTimeout(timeout)
        }
    }, [trigger, onComplete, windowSize.width])

    // Calculate confetti source position (confetti shoots UP from here)
    const confettiSource = {
        x: (sourceX ?? windowSize.width / 2) - sourceWidth / 2,
        y: sourceY ?? windowSize.height * 0.3,
        w: sourceWidth,
        h: 5
    }

    if (!showConfetti || windowSize.width === 0) return null

    return (
        <Confetti
            width={windowSize.width}
            height={windowSize.height}
            numberOfPieces={120}
            confettiSource={confettiSource}
            initialVelocityY={48}
            initialVelocityX={6}
            gravity={0.5}
            wind={0}
            friction={0.97}
            colors={["#a864fd", "#29cdff", "#78ff44", "#ff718d", "#fdff6a", "#f97316", "#ec4899"]}
            recycle={false}
            tweenDuration={50}
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                zIndex,
                pointerEvents: "none"
            }}
        />
    )
}
